import { useState, useEffect, useCallback } from 'react'
import { Plus, GripVertical, Eye, EyeOff, Trash2, Edit2, ArrowUp, ArrowDown, X } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import CollectionEditor from './CollectionEditor'
import CollectionPreview from './CollectionPreview'
import './Collections.css'

export default function Collections() {
  const { api } = useAuth()
  
  const [collections, setCollections] = useState([])
  const [allTags, setAllTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  
  const [selected, setSelected] = useState(null)
  const [showCreate, setShowCreate] = useState(false)
  const [showPreview, setShowPreview] = useState(null) // collection id
  
  // Drag state
  const [draggedId, setDraggedId] = useState(null)
  const [dragOverId, setDragOverId] = useState(null)

  const loadCollections = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api('/vod/collections')
      setCollections(data.collections || [])
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setLoading(false)
  }, [api])

  const loadTags = useCallback(async () => {
    try {
      const data = await api('/tags')
      setAllTags(data.tags || [])
    } catch {}
  }, [api])

  useEffect(() => {
    loadCollections()
    loadTags()
  }, [loadCollections, loadTags])

  async function handleToggleActive(collection) {
    try {
      await api(`/vod/collections/${collection.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: collection.is_active ? 0 : 1 })
      })
      setStatus(collection.is_active ? '✅ Collection hidden' : '✅ Collection visible')
      loadCollections()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleDelete(id) {
    if (!confirm('Delete this collection?')) return
    
    try {
      await api(`/vod/collections/${id}`, { method: 'DELETE' })
      setStatus('✅ Collection deleted')
      if (selected?.id === id) setSelected(null)
      loadCollections()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  // Drag and drop reordering
  function handleDragStart(e, id) {
    setDraggedId(id)
    e.dataTransfer.effectAllowed = 'move'
  }

  function handleDragOver(e, id) {
    e.preventDefault()
    if (id !== draggedId) {
      setDragOverId(id)
    }
  }

  function handleDragLeave() {
    setDragOverId(null)
  }

  async function handleDrop(e, targetId) {
    e.preventDefault()
    setDragOverId(null)
    
    if (draggedId === targetId) {
      setDraggedId(null)
      return
    }

    // Calculate new order
    const oldIndex = collections.findIndex(c => c.id === draggedId)
    const newIndex = collections.findIndex(c => c.id === targetId)
    
    if (oldIndex === -1 || newIndex === -1) {
      setDraggedId(null)
      return
    }

    // Reorder locally first for instant feedback
    const reordered = [...collections]
    const [moved] = reordered.splice(oldIndex, 1)
    reordered.splice(newIndex, 0, moved)
    setCollections(reordered)
    setDraggedId(null)

    // Save new order to backend
    try {
      await api('/vod/collections/reorder', {
        method: 'POST',
        body: JSON.stringify({ order: reordered.map(c => c.id) })
      })
      setStatus('✅ Order saved')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
      loadCollections() // Reload on error
    }
  }

  function handleDragEnd() {
    setDraggedId(null)
    setDragOverId(null)
  }

  // Move up/down buttons (alternative to drag)
  async function moveCollection(id, direction) {
    const index = collections.findIndex(c => c.id === id)
    if (index === -1) return
    
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= collections.length) return

    const reordered = [...collections]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(newIndex, 0, moved)
    setCollections(reordered)

    try {
      await api('/vod/collections/reorder', {
        method: 'POST',
        body: JSON.stringify({ order: reordered.map(c => c.id) })
      })
    } catch (e) {
      setStatus(`Error: ${e.message}`)
      loadCollections()
    }
  }

  function handleCreateClick() {
    setSelected(null)
    setShowCreate(true)
  }

  function handleEdit(collection) {
    setShowCreate(false)
    setSelected(collection)
  }

  function handleSaveComplete() {
    setSelected(null)
    setShowCreate(false)
    loadCollections()
  }

  return (
    <div className="collections-page">
      <div className="collections-header">
        <div className="collections-header-left">
          <h1>Collections</h1>
          <span className="collections-count">{collections.length} collection{collections.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="collections-header-right">
          <button className="btn-primary" onClick={handleCreateClick}>
            <Plus size={16} /> New Collection
          </button>
        </div>
      </div>

      {status && (
        <div className={`collections-status ${status.startsWith('Error') ? 'error' : 'success'}`}>
          {status}
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      <div className="collections-layout">
        {/* Collections List */}
        <div className="collections-list-panel">
          <div className="collections-list-header">
            <span>Drag to reorder • Order determines display in apps</span>
          </div>

          {loading ? (
            <div className="collections-list-empty">Loading...</div>
          ) : collections.length === 0 ? (
            <div className="collections-list-empty">
              No collections yet. Click "New Collection" to create one.
            </div>
          ) : (
            <div className="collections-list">
              {collections.map((col, index) => (
                <div
                  key={col.id}
                  className={`collection-item ${draggedId === col.id ? 'dragging' : ''} ${dragOverId === col.id ? 'drag-over' : ''} ${selected?.id === col.id ? 'selected' : ''}`}
                  draggable
                  onDragStart={e => handleDragStart(e, col.id)}
                  onDragOver={e => handleDragOver(e, col.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={e => handleDrop(e, col.id)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="collection-drag-handle">
                    <GripVertical size={16} />
                  </div>
                  
                  <div className="collection-order">{index + 1}</div>
                  
                  <div className="collection-info" onClick={() => handleEdit(col)}>
                    <div className="collection-name">{col.name}</div>
                    <div className="collection-meta">
                      <span className="collection-tags-preview">
                        {col.query_tags.split(',').slice(0, 3).join(', ')}
                        {col.query_tags.split(',').length > 3 && '...'}
                      </span>
                      {col.ads_enabled ? (
                        <span className="collection-ads-badge">📺 Ads</span>
                      ) : null}
                      <span className={`collection-active ${col.is_active ? 'active' : 'inactive'}`}>
                        {col.is_active ? <Eye size={12} /> : <EyeOff size={12} />}
                      </span>
                    </div>
                  </div>

                  <div className="collection-actions">
                    <button
                      className="action-btn labeled"
                      onClick={() => moveCollection(col.id, 'up')}
                      disabled={index === 0}
                    >
                      <ArrowUp size={13} />
                      <span>Up</span>
                    </button>
                    <button
                      className="action-btn labeled"
                      onClick={() => moveCollection(col.id, 'down')}
                      disabled={index === collections.length - 1}
                    >
                      <ArrowDown size={13} />
                      <span>Dn</span>
                    </button>
                    <button
                      className="action-btn labeled"
                      onClick={() => setShowPreview(col.id)}
                    >
                      <Eye size={13} />
                      <span>Preview</span>
                    </button>
                    <button
                      className="action-btn labeled"
                      onClick={() => handleEdit(col)}
                    >
                      <Edit2 size={13} />
                      <span>Edit</span>
                    </button>
                    <button
                      className="action-btn labeled danger"
                      onClick={() => handleDelete(col.id)}
                    >
                      <Trash2 size={13} />
                      <span>Del</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Editor Panel */}
        <div className="collections-editor-panel">
          {showCreate ? (
            <CollectionEditor
              allTags={allTags}
              onSave={handleSaveComplete}
              onCancel={() => setShowCreate(false)}
              setStatus={setStatus}
            />
          ) : selected ? (
            <CollectionEditor
              collection={selected}
              allTags={allTags}
              onSave={handleSaveComplete}
              onCancel={() => setSelected(null)}
              setStatus={setStatus}
            />
          ) : (
            <div className="collections-empty">
              <p>Select a collection to edit or create a new one.</p>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <CollectionPreview
          collectionId={showPreview}
          onClose={() => setShowPreview(null)}
        />
      )}
    </div>
  )
}
